/**
 * Which surface the file panel opens for a workspace path, decided from the
 * extension alone so it can be tested as a table. Everything but `text` skips
 * the workspace text read: those viewers fetch bytes from a signed asset URL,
 * and routing a binary through the text read is how a synced `.zip` or `.mp4`
 * used to end as a Download button. Extensions are disjoint, so order here
 * only matters for readability.
 */
import {
  isWorkspaceArchivePreviewPath,
  isWorkspaceAudioPreviewPath,
  isWorkspaceDocumentPreviewPath,
  isWorkspaceImagePreviewPath,
  isWorkspacePresentationPreviewPath,
  isWorkspaceSpreadsheetPreviewPath,
  isWorkspaceVideoPreviewPath,
} from "@amoreg/shared/filePreview";

export type WorkspacePreviewSurface =
  | "image"
  | "document"
  | "spreadsheet"
  | "presentation"
  | "archive"
  | "video"
  | "audio"
  | "pdf"
  | "html"
  | "text";

/**
 * How the panel's zoom applies: CSS `zoom` reflows text, tables and
 * spreadsheets; an image is sized by the value directly; the PDF viewer and
 * the slide fit take the value themselves; the Office viewer and the media
 * players cannot be zoomed, so the controls are hidden.
 */
export type WorkspacePreviewZoomMode = "pdf" | "presentation" | "image" | "none" | "css";

export function workspacePreviewSurface(relativePath: string): WorkspacePreviewSurface {
  if (isWorkspaceImagePreviewPath(relativePath)) return "image";
  if (isWorkspaceDocumentPreviewPath(relativePath)) return "document";
  if (isWorkspaceSpreadsheetPreviewPath(relativePath)) return "spreadsheet";
  if (isWorkspacePresentationPreviewPath(relativePath)) return "presentation";
  if (isWorkspaceArchivePreviewPath(relativePath)) return "archive";
  if (isWorkspaceVideoPreviewPath(relativePath)) return "video";
  if (isWorkspaceAudioPreviewPath(relativePath)) return "audio";
  if (/\.pdf$/i.test(relativePath)) return "pdf";
  if (/\.html?$/i.test(relativePath)) return "html";
  return "text";
}

export function workspacePreviewZoomMode(
  surface: WorkspacePreviewSurface,
): WorkspacePreviewZoomMode {
  switch (surface) {
    case "pdf":
      return "pdf";
    case "presentation":
      return "presentation";
    case "image":
      return "image";
    case "document":
    case "video":
    case "audio":
      return "none";
    default:
      return "css";
  }
}
